import { RegionsEnum, RegionsEnumRuntype } from '@luminovo/api-contracts/regions';
import { z } from 'zod/v4';

import { HISTORICAL_OFFER_PRICE_TYPES } from '../historicalOfferPriceTypes';
import { MarketSavingsPriceBreakSchema } from './priceBreaks';
import { MarketSavingsStandardPriceSnapshotSchema } from './standardPriceSnapshot';

export const MARKET_SAVINGS_REPORT_METRICS_COMPUTATION = 'market-savings-report-metrics';
export const MARKET_SAVINGS_REPORT_METRICS_VERSION = 8;
export const MARKET_SAVINGS_REPORT_READABLE_METRICS_VERSIONS = [
    MARKET_SAVINGS_REPORT_METRICS_VERSION,
    7,
    6,
    5,
] as const;
export const MarketSavingsReportReadableMetricsVersionSchema = z.union([
    z.literal(MARKET_SAVINGS_REPORT_READABLE_METRICS_VERSIONS[0]),
    z.literal(MARKET_SAVINGS_REPORT_READABLE_METRICS_VERSIONS[1]),
    z.literal(MARKET_SAVINGS_REPORT_READABLE_METRICS_VERSIONS[2]),
    z.literal(MARKET_SAVINGS_REPORT_READABLE_METRICS_VERSIONS[3]),
]);
export type MarketSavingsReportReadableMetricsVersion = z.infer<typeof MarketSavingsReportReadableMetricsVersionSchema>;
export const MARKET_SAVINGS_DETAIL_MAX_LIMIT = 25_000;
export const MARKET_SAVINGS_SCATTER_LIMIT = 1_200;
export const MARKET_SAVINGS_TOP_OPPORTUNITY_LIMIT = 20;

export const MarketSavingsMetrics = {
    marketP50Price: 'marketP50Price',
    marketP10Price: 'marketP10Price',
    marketP1Price: 'marketP1Price',
    demandQualifiedMarketMinPrice: 'demandQualifiedMarketMinPrice',
    lastPurchasePrice: 'lastPurchasePrice',
    cheapestPurchasePrice: 'cheapestPurchasePrice',
    latestStandardPrice: 'latestStandardPrice',
    latestContractPrice: 'latestContractPrice',
} as const;

export const ReportMetricIds = [
    MarketSavingsMetrics.marketP50Price,
    MarketSavingsMetrics.marketP10Price,
    MarketSavingsMetrics.marketP1Price,
    MarketSavingsMetrics.demandQualifiedMarketMinPrice,
    MarketSavingsMetrics.lastPurchasePrice,
    MarketSavingsMetrics.cheapestPurchasePrice,
    MarketSavingsMetrics.latestStandardPrice,
    MarketSavingsMetrics.latestContractPrice,
] as const;

export const ReportMetricIdSchema = z.enum(ReportMetricIds);
export type ReportMetricId = z.infer<typeof ReportMetricIdSchema>;

const EnabledReportMetricsSchema = z
    .array(ReportMetricIdSchema)
    .min(1)
    .refine((metrics) => new Set(metrics).size === metrics.length, 'Enabled metrics must be unique');

export const PriceTypeSchema = z.enum(HISTORICAL_OFFER_PRICE_TYPES);
export type PriceType = z.infer<typeof PriceTypeSchema>;

export const TimeWindowSchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
});
export type TimeWindow = z.infer<typeof TimeWindowSchema>;

export const PartLibrarySourceSchema = z.enum(['calculation-tree', 'component-api']);

export const MarketSavingsDemandConfigSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('api') }),
    z.object({
        mode: z.literal('fixed'),
        fixedDemandPerIpn: z.number().positive(),
    }),
]);
export type MarketSavingsDemandConfig = z.infer<typeof MarketSavingsDemandConfigSchema>;

export const MarketSavingsTrustedSuppliersSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('default'),
        settingsVersion: z.string().min(1),
    }),
    z.object({
        mode: z.literal('custom'),
        names: z.array(z.string().min(1)).min(1),
    }),
]);
export const MarketSavingsExternalPurchasePricesSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('none') }),
    z.object({
        mode: z.literal('managed'),
        sourceId: z.string().min(1),
        importVersion: z.string().min(1),
    }),
]);
export const MarketSavingsExecutionOptionsSchema = z.object({
    metricsBatchPartCount: z.number().int().positive(),
    ipnOffersBatchSize: z.number().int().positive(),
    ipnOffersConcurrency: z.number().int().positive(),
    detailSelectionLimit: z.number().int().min(1).max(MARKET_SAVINGS_DETAIL_MAX_LIMIT),
    detailsOfferPartChunkSize: z.number().int().positive(),
    offerQueryConcurrency: z.number().int().positive(),
});
// Metric versions are stored as ClickHouse UInt32 values.
const MetricVersionSchema = z.int().nonnegative().max(4_294_967_295);

/** Read versions pinned together with the weekly bucket for every shard and retry of a report. */
export const MarketSavingsPersistedMetricsReadVersionsSchema = z.object({
    demand: MetricVersionSchema,
    marketPrices: MetricVersionSchema,
    internalPrices: MetricVersionSchema,
    marketPricesByBreak: MetricVersionSchema,
    internalPricesByBreak: MetricVersionSchema,
});
export type MarketSavingsPersistedMetricsReadVersions = z.infer<typeof MarketSavingsPersistedMetricsReadVersionsSchema>;

export const MarketSavingsSchedulerInputSchema = z.object({
    jobKey: z.string().min(1),
    // Optional only for decoding reports created before run-scoped immutable artifacts were introduced.
    artifactRunId: z.string().uuid().optional(),
    root: z
        .object({
            type: z.string().min(1),
            id: z.string().min(1),
        })
        .optional(),
    timeWindow: TimeWindowSchema,
    // Fresh inputs omit the snapshot; planning permanently resolves and pins it before scheduling shards.
    persistedMetricsEffectiveBucket: z.iso.datetime().nullable().optional(),
    // Also accepts legacy queued inputs. Only their read-time fallback is temporary:
    // https://linear.app/luminovo/issue/PRI-692. Direct reader resolution remains supported.
    persistedMetricsReadVersions: MarketSavingsPersistedMetricsReadVersionsSchema.optional(),
    currency: z.string().min(1),
    enabledMetrics: EnabledReportMetricsSchema,
    marketPriceTypes: z.array(PriceTypeSchema).min(1),
    purchasePriceTypes: z.array(PriceTypeSchema).min(1),
    partLibrarySource: PartLibrarySourceSchema,
    calculationTreeVersion: z.number().int().positive().optional(),
    demand: MarketSavingsDemandConfigSchema,
    trustedSuppliers: MarketSavingsTrustedSuppliersSchema,
    externalPurchasePrices: MarketSavingsExternalPurchasePricesSchema,
    executionOptions: MarketSavingsExecutionOptionsSchema,
});
export type MarketSavingsSchedulerInput = z.infer<typeof MarketSavingsSchedulerInputSchema>;

export const MarketSavingsPartMatchClassificationSchema = z.enum([
    'match',
    'form-fit-function',
    'functional',
    'used-by-others',
]);
export type MarketSavingsPartMatchClassification = z.infer<typeof MarketSavingsPartMatchClassificationSchema>;

export const MarketSavingsPartSchema = z.object({
    id: z.string().min(1),
    ipns: z.array(z.string().min(1)).min(1),
    mpn: z.string().nullable().optional(),
    manufacturerId: z.string().nullable().optional(),
    manufacturerName: z.string(),
    description: z.string().nullable().optional(),
    matchClassificationByIpn: z.record(z.string(), MarketSavingsPartMatchClassificationSchema).optional(),
});
export type MarketSavingsPart = z.infer<typeof MarketSavingsPartSchema>;

export const MarketSavingsCompactPartSchema = z.object({
    id: z.string().min(1),
    mpn: z.string(),
    manufacturerName: z.string(),
    description: z.string(),
    matchClassification: MarketSavingsPartMatchClassificationSchema.optional(),
});
export type MarketSavingsCompactPart = z.infer<typeof MarketSavingsCompactPartSchema>;

export const MarketSavingsIpnGroupSchema = z.object({
    ipn: z.string().min(1),
    parts: z.array(MarketSavingsCompactPartSchema).min(1),
});
export type MarketSavingsIpnGroup = z.infer<typeof MarketSavingsIpnGroupSchema>;

export const MarketSavingsShardGroupsSchema = z.object({
    jobKey: z.string().min(1),
    groups: z.array(MarketSavingsIpnGroupSchema),
});
export const MarketSavingsSupplierSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
});
export type MarketSavingsSupplier = z.infer<typeof MarketSavingsSupplierSchema>;

export const MarketSavingsSupplierSnapshotSchema = z.object({
    jobKey: z.string().min(1),
    suppliers: z.array(MarketSavingsSupplierSchema),
});
export type MarketSavingsSupplierSnapshot = z.infer<typeof MarketSavingsSupplierSnapshotSchema>;

export const MarketSavingsArtifactPointerSchema = z.object({
    key: z.string().min(1),
});
export type MarketSavingsArtifactPointer = z.infer<typeof MarketSavingsArtifactPointerSchema>;

export const MarketSavingsMetricsShardPayloadSchema = z.object({
    jobKey: z.string().min(1),
    shardGroupsBlobKey: z.string().min(1),
    supplierSnapshotBlobKey: z.string().min(1),
    schedulerInputBlobKey: z.string().min(1),
    batchIndex: z.number().int().nonnegative(),
    ipnStart: z.number().int().nonnegative(),
    ipnEnd: z.number().int().nonnegative(),
    partCount: z.number().int().nonnegative(),
});
export type MarketSavingsMetricsShardPayload = z.infer<typeof MarketSavingsMetricsShardPayloadSchema>;

export const MarketSavingsMetricsCheckpointSchema = z
    .object({
        completedPhase: z.enum(['demand', 'tenantOffers', 'marketMetrics', 'alternatives', 'upload']).optional(),
        partialBlobKey: z.string().min(1).optional(),
    })
    .nullable();
export type MarketSavingsMetricsCheckpoint = z.infer<typeof MarketSavingsMetricsCheckpointSchema>;

/**
 * The demand behind `demandQuantity`, split by delivery end date and sorted by it. ERPs that send nested
 * horizons (a 6-month and a 12-month demand from the same day) make `demandQuantity` their sum, so a
 * scenario can pick one horizon from here instead.
 */
export const MarketSavingsDemandByDeliveryEndSchema = z.array(
    z.object({
        deliveryEndDate: z.iso.date(),
        quantity: z.number().nonnegative(),
    }),
);
export type MarketSavingsDemandByDeliveryEnd = z.infer<typeof MarketSavingsDemandByDeliveryEndSchema>;

export const MarketSavingsReportRowMetricsSchema = z
    .object({
        marketP50Price: z.number().positive().nullable().optional(),
        marketP10Price: z.number().positive().nullable().optional(),
        marketP1Price: z.number().positive().nullable().optional(),
        demandQualifiedMarketMinPrice: z.number().positive().nullable().optional(),
        lastPurchasePrice: z.number().positive().nullable().optional(),
        cheapestPurchasePrice: z.number().positive().nullable().optional(),
        latestStandardPrice: z.number().positive().nullable().optional(),
        latestContractPrice: z.number().positive().nullable().optional(),
    })
    .strict();
export type MarketSavingsReportRowMetrics = z.infer<typeof MarketSavingsReportRowMetricsSchema>;

export const MarketSavingsReportRowSchema = z.object({
    /** Absent on legacy reports, whose market population includes imported offers. */
    marketPopulationPolicy: z.literal('exclude-import').optional(),
    ipn: z.string().min(1),
    parts: z.array(MarketSavingsCompactPartSchema),
    demandQuantity: z.number().positive().nullable(),
    // Optional only for decoding rows written before demand was split by delivery end date.
    demandByDeliveryEnd: MarketSavingsDemandByDeliveryEndSchema.optional(),
    /** Prices at the break of `demandQuantity`, the demand every metric was qualified with. */
    metrics: MarketSavingsReportRowMetricsSchema,
    /**
     * The same prices at every price break, so a scenario can price another demand. Absent on rows
     * written before prices were stored per break, or while those prices are not computed yet.
     */
    metricsByBreak: z.partialRecord(MarketSavingsPriceBreakSchema, MarketSavingsReportRowMetricsSchema).optional(),
    standardPriceSnapshot: MarketSavingsStandardPriceSnapshotSchema.optional(),
    contractPriceSnapshot: MarketSavingsStandardPriceSnapshotSchema.optional(),
    warnings: z.array(z.string()),
});
export type MarketSavingsReportRow = z.infer<typeof MarketSavingsReportRowSchema>;

export const MarketSavingsDetailPriceBreakSchema = z.object({
    quantity: z.number().nonnegative(),
    unitPrice: z.number().positive(),
});
export const MarketSavingsDetailOfferSchema = z.object({
    id: z.string().min(1),
    partId: z.string().min(1).optional(),
    priceType: PriceTypeSchema,
    supplier: z
        .object({
            id: z.string().min(1),
            name: z.string().min(1),
            // Older persisted reports stored UUIDs here. Normalize them at the read boundary.
            stockLocation: RegionsEnumRuntype.exclude([RegionsEnum.Unknown]).optional().catch(undefined),
        })
        .optional(),
    provenance: z.object({
        source: z.enum(['historical-offers', 'tenant-offer-api']),
        scope: z.enum(['tenant', 'public']),
        label: z.string().min(1).optional(),
    }),
    date: z.iso.date().optional(),
    stock: z.number().nonnegative().optional(),
    leadTimeDays: z.number().nonnegative().optional(),
    priceBreaks: z.array(MarketSavingsDetailPriceBreakSchema),
});
export type MarketSavingsDetailOffer = z.infer<typeof MarketSavingsDetailOfferSchema>;

export const MarketSavingsDetailEvidenceSchema = z.object({
    parts: z.array(MarketSavingsCompactPartSchema).min(1),
    demandQuantity: z.number().positive().nullable(),
    metricValues: z.partialRecord(ReportMetricIdSchema, z.number().positive().nullable()),
    marketOffers: z.array(MarketSavingsDetailOfferSchema),
    purchaseOffers: z.array(MarketSavingsDetailOfferSchema),
    standardPriceOffers: z.array(MarketSavingsDetailOfferSchema),
    standardPriceSnapshot: MarketSavingsStandardPriceSnapshotSchema.optional(),
    contractPriceOffers: z.array(MarketSavingsDetailOfferSchema).optional(),
    contractPriceSnapshot: MarketSavingsStandardPriceSnapshotSchema.optional(),
});
export type MarketSavingsDetailEvidence = z.infer<typeof MarketSavingsDetailEvidenceSchema>;

const MarketSavingsDetailIdentityShape = {
    tenant: z.string().min(1),
    jobKey: z.string().min(1),
    enabledMetrics: EnabledReportMetricsSchema,
    timeWindow: TimeWindowSchema,
    ipn: z.string().min(1),
};

export const MarketSavingsDetailIdentitySchema = z.object({
    ...MarketSavingsDetailIdentityShape,
    metricsComputationVersion: z.literal(MARKET_SAVINGS_REPORT_METRICS_VERSION),
});
export type MarketSavingsDetailIdentity = z.infer<typeof MarketSavingsDetailIdentitySchema>;

export const MarketSavingsReadableDetailIdentitySchema = z.object({
    ...MarketSavingsDetailIdentityShape,
    metricsComputationVersion: MarketSavingsReportReadableMetricsVersionSchema,
});
export type MarketSavingsReadableDetailIdentity = z.infer<typeof MarketSavingsReadableDetailIdentitySchema>;

function marketSavingsDetailArtifactSchema<TIdentity extends z.ZodType>(identitySchema: TIdentity) {
    return z.discriminatedUnion('state', [
        z.object({
            state: z.literal('available'),
            identity: identitySchema,
            evidence: MarketSavingsDetailEvidenceSchema,
        }),
        z.object({
            state: z.literal('failed'),
            identity: identitySchema,
            errorCode: z.string().min(1),
        }),
    ]);
}

/** Producer schema. Its identity is intentionally restricted to the current computation version. */
export const MarketSavingsDetailArtifactSchema = marketSavingsDetailArtifactSchema(MarketSavingsDetailIdentitySchema);
/** Decode schema for artifacts persisted by explicitly supported computation versions. */
export const MarketSavingsReadableDetailArtifactSchema = marketSavingsDetailArtifactSchema(
    MarketSavingsReadableDetailIdentitySchema,
);
export const MarketSavingsMetricsShardResultSchema = z.object({
    batchIndex: z.number().int().nonnegative(),
    rowCount: z.number().int().nonnegative(),
    rowsBlobKey: z.string().min(1),
    schedulerInputBlobKey: z.string().min(1),
    supplierSnapshotBlobKey: z.string().min(1),
    warnings: z.array(z.string()),
    summarySource: z.literal('persisted-weekly-metrics'),
    effectiveBucket: z.iso.datetime().nullable(),
    /** The distinct `demandByDeliveryEnd` dates of this shard's rows; absent when they have none to split. */
    demandDeliveryEndDates: z.array(z.iso.date()).optional(),
});
export type MarketSavingsMetricsShardResult = z.infer<typeof MarketSavingsMetricsShardResultSchema>;

const MarketSavingsReportMetadataShape = {
    tenant: z.string().min(1),
    jobKey: z.string().min(1),
    currency: z.string().min(1),
    timeWindow: TimeWindowSchema,
    generatedAt: z.string().min(1),
    enabledMetrics: EnabledReportMetricsSchema,
    warnings: z.array(z.string()),
    /**
     * Every distinct `demandByDeliveryEnd` date in the rows, sorted. A scenario only changes when its date
     * crosses one of them. Absent when the rows carry no split, as on reports calculated before they did.
     */
    demandDeliveryEndDates: z.array(z.iso.date()).optional(),
};
const MarketSavingsPersistedMetricsMetadataShape = {
    source: z.literal('persisted-weekly-metrics'),
    effectiveBucket: z.iso.datetime().nullable(),
};

export const MarketSavingsReportMetadataSchema = z.object({
    ...MarketSavingsReportMetadataShape,
    ...MarketSavingsPersistedMetricsMetadataShape,
});
export type MarketSavingsReportMetadata = z.infer<typeof MarketSavingsReportMetadataSchema>;

export const MarketSavingsReadableReportMetadataSchema = z.object({
    ...MarketSavingsReportMetadataShape,
    source: MarketSavingsPersistedMetricsMetadataShape.source.optional(),
    effectiveBucket: MarketSavingsPersistedMetricsMetadataShape.effectiveBucket.optional(),
});
export type MarketSavingsReadableReportMetadata = z.infer<typeof MarketSavingsReadableReportMetadataSchema>;

export const MarketSavingsTriggerCalculationInputSchema = z.object({ requestId: z.uuid() });
export const MarketSavingsTriggerCalculationOutputSchema = z.object({ jobKey: z.string() });

/** The subset of the stored report metadata the latest-report endpoint hands to the browser. */
export const MarketSavingsReportPayloadMetadataSchema = MarketSavingsReadableReportMetadataSchema.pick({
    jobKey: true,
    currency: true,
    generatedAt: true,
    enabledMetrics: true,
    warnings: true,
    source: true,
    effectiveBucket: true,
    demandDeliveryEndDates: true,
});
export const MarketSavingsReportPayloadSchema = z.object({
    rowCount: z.number().int().nonnegative(),
    metadata: MarketSavingsReportPayloadMetadataSchema,
});
export type MarketSavingsReportPayload = z.infer<typeof MarketSavingsReportPayloadSchema>;

export const MarketSavingsSummaryManifestChunkSchema = z.object({
    batchIndex: z.number().int().nonnegative(),
    rowCount: z.number().int().nonnegative(),
    rowsBlobKey: z.string().min(1),
});
const MarketSavingsSummaryManifestShape = {
    tenant: z.string().min(1),
    jobKey: z.string().min(1),
    enabledMetrics: EnabledReportMetricsSchema,
    totalRowCount: z.number().int().nonnegative(),
    chunks: z.array(MarketSavingsSummaryManifestChunkSchema),
};

function marketSavingsSummaryManifestSchema<TVersion extends z.ZodType>(versionSchema: TVersion) {
    return z
        .object({
            ...MarketSavingsSummaryManifestShape,
            metricsComputationVersion: versionSchema,
        })
        .superRefine((manifest, ctx) => {
            const total = manifest.chunks.reduce((sum, chunk) => sum + chunk.rowCount, 0);
            if (total !== manifest.totalRowCount) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Manifest chunk counts must equal totalRowCount',
                });
            }
        });
}

export const MarketSavingsSummaryManifestSchema = marketSavingsSummaryManifestSchema(
    z.literal(MARKET_SAVINGS_REPORT_METRICS_VERSION),
);
export type MarketSavingsSummaryManifest = z.infer<typeof MarketSavingsSummaryManifestSchema>;

export const MarketSavingsReadableSummaryManifestSchema = marketSavingsSummaryManifestSchema(
    MarketSavingsReportReadableMetricsVersionSchema,
);
export type MarketSavingsReadableSummaryManifest = z.infer<typeof MarketSavingsReadableSummaryManifestSchema>;

export const MarketSavingsDetailManifestEntrySchema = z.object({
    ipn: z.string().min(1),
    artifactBlobKey: z.string().min(1),
});
const MarketSavingsDetailManifestShape = {
    tenant: z.string().min(1),
    jobKey: z.string().min(1),
    selectionPolicy: z.literal('maximum-positive-opportunity-across-distinct-enabled-metric-pairs'),
    enabledMetrics: EnabledReportMetricsSchema,
    timeWindow: TimeWindowSchema,
    requestedLimit: z.number().int().min(1).max(MARKET_SAVINGS_DETAIL_MAX_LIMIT),
    positiveCandidateCount: z.number().int().nonnegative(),
    entries: z.array(MarketSavingsDetailManifestEntrySchema),
};

export const MarketSavingsDetailManifestSchema = z.object({
    ...MarketSavingsDetailManifestShape,
    metricsComputationVersion: z.literal(MARKET_SAVINGS_REPORT_METRICS_VERSION),
});
export type MarketSavingsDetailManifest = z.infer<typeof MarketSavingsDetailManifestSchema>;

export const MarketSavingsReadableDetailManifestSchema = z.object({
    ...MarketSavingsDetailManifestShape,
    metricsComputationVersion: MarketSavingsReportReadableMetricsVersionSchema,
});
export type MarketSavingsReadableDetailManifest = z.infer<typeof MarketSavingsReadableDetailManifestSchema>;

export const MarketSavingsMetricsJobResultSchema = z
    .object({
        jobKey: z.string().min(1),
        artifactRunId: z.string().uuid().optional(),
        rowCount: z.number().int().nonnegative(),
        summaryManifest: MarketSavingsSummaryManifestSchema,
        detailManifest: MarketSavingsDetailManifestSchema,
        schedulerInput: MarketSavingsArtifactPointerSchema.optional(),
        supplierSnapshot: MarketSavingsArtifactPointerSchema.optional(),
        metadata: MarketSavingsReportMetadataSchema,
        /**
         * The reduce step read every summary chunk and matched each one against its shard's row
         * count before publishing. Readers skip their own chunk validation only when this is set.
         */
        summaryChunksVerified: z.literal(true),
    })
    .superRefine((result, ctx) => {
        const manifest = result.summaryManifest;
        if (manifest.totalRowCount !== result.rowCount) {
            ctx.addIssue({
                code: 'custom',
                message: 'Manifest totalRowCount must equal result rowCount',
            });
        }
    });
export type MarketSavingsMetricsJobResult = z.infer<typeof MarketSavingsMetricsJobResultSchema>;

/**
 * Decode schema for persisted report results. Keep this separate from the producer schema above so
 * scheduling version 7 cannot accidentally publish a result carrying a legacy identity. Legacy
 * data-quality fields are intentionally not decoded: v5/v6 reports remain readable without loading
 * or validating their data-quality artifacts.
 */
export const MarketSavingsReadableMetricsJobResultSchema = z
    .object({
        jobKey: z.string().min(1),
        artifactRunId: z.string().uuid().optional(),
        rowCount: z.number().int().nonnegative(),
        summaryManifest: MarketSavingsReadableSummaryManifestSchema,
        detailManifest: MarketSavingsReadableDetailManifestSchema,
        schedulerInput: MarketSavingsArtifactPointerSchema.optional(),
        supplierSnapshot: MarketSavingsArtifactPointerSchema.optional(),
        metadata: MarketSavingsReadableReportMetadataSchema,
        // Absent on reports published before the reduce step verified chunks (including v5/v6).
        summaryChunksVerified: z.literal(true).optional(),
    })
    .superRefine((result, ctx) => {
        const summary = result.summaryManifest;
        const detail = result.detailManifest;
        const metadata = result.metadata;

        if (summary.totalRowCount !== result.rowCount) {
            ctx.addIssue({
                code: 'custom',
                message: 'Manifest totalRowCount must equal result rowCount',
            });
        }
        if (
            result.jobKey !== summary.jobKey ||
            result.jobKey !== detail.jobKey ||
            result.jobKey !== metadata.jobKey ||
            summary.tenant !== detail.tenant ||
            summary.tenant !== metadata.tenant ||
            summary.metricsComputationVersion !== detail.metricsComputationVersion ||
            !sameStringValues(summary.enabledMetrics, detail.enabledMetrics) ||
            !sameStringValues(summary.enabledMetrics, metadata.enabledMetrics) ||
            !sameTimeWindow(detail.timeWindow, metadata.timeWindow)
        ) {
            ctx.addIssue({
                code: 'custom',
                message: 'Persisted market savings report identities must be consistent',
            });
        }
        if (
            summary.metricsComputationVersion >= 6 &&
            (metadata.source === undefined || metadata.effectiveBucket === undefined)
        ) {
            ctx.addIssue({
                code: 'custom',
                message: 'Version 6 and 7 report metadata must include source and effectiveBucket',
            });
        }
    });
export type MarketSavingsReadableMetricsJobResult = z.infer<typeof MarketSavingsReadableMetricsJobResultSchema>;

export const MarketSavingsDetailInputSchema = z.object({
    jobKey: z.string().min(1),
    ipn: z.string().min(1),
});
const MarketSavingsDetailSelectionMetadataSchema = z.object({
    selectionPolicy: z.literal('maximum-positive-opportunity-across-distinct-enabled-metric-pairs'),
    requestedLimit: z.number().int().min(1).max(MARKET_SAVINGS_DETAIL_MAX_LIMIT),
    positiveCandidateCount: z.number().int().nonnegative(),
    selectedCount: z.number().int().nonnegative(),
});

export const MarketSavingsDetailAvailabilitySchema = z.discriminatedUnion('availability', [
    z.object({
        availability: z.literal('available'),
        identity: MarketSavingsReadableDetailIdentitySchema,
        evidence: MarketSavingsDetailEvidenceSchema,
    }),
    z.object({
        availability: z.literal('not-selected'),
        ipn: z.string().min(1),
        selection: MarketSavingsDetailSelectionMetadataSchema,
    }),
    z.object({
        availability: z.literal('failed'),
        ipn: z.string().min(1),
        errorCode: z.string().min(1),
        selection: MarketSavingsDetailSelectionMetadataSchema,
    }),
]);
export type MarketSavingsDetailAvailability = z.infer<typeof MarketSavingsDetailAvailabilitySchema>;

export const MarketSavingsScenarioInputSchema = z
    .object({
        jobKey: z.string().min(1),
        baselineMetric: ReportMetricIdSchema,
        comparisonMetric: ReportMetricIdSchema,
        /**
         * Use, per IPN, the demand that ends latest on or before this date instead of all demand.
         * IPNs without such demand have none in the scenario.
         */
        demandDeliveryEndDate: z.iso.date().optional(),
        offset: z
            .number()
            .int()
            .nonnegative()
            .max(Number.MAX_SAFE_INTEGER - 200)
            .default(0),
        pageSize: z.number().int().min(1).max(200).default(50),
    })
    .refine((input) => input.baselineMetric !== input.comparisonMetric, {
        message: 'Baseline and comparison metrics must be distinct',
        path: ['comparisonMetric'],
    });
export const MarketSavingsScenarioRowSchema = z.object({
    ipn: z.string().min(1),
    parts: z.array(MarketSavingsCompactPartSchema),
    warnings: z.array(z.string()),
    demandQuantity: z.number().positive().nullable(),
    /** The price break the unit prices are taken at; absent on scenarios cached before it was recorded. */
    priceBreak: MarketSavingsPriceBreakSchema.nullable().optional(),
    baselineUnitPrice: z.number().positive().nullable(),
    comparisonUnitPrice: z.number().positive().nullable(),
    unitDelta: z.number().nullable(),
    baselineSpend: z.number().nonnegative().nullable(),
    comparisonSpend: z.number().nonnegative().nullable(),
    projectedDelta: z.number().nullable(),
    savingsOpportunity: z.number().nonnegative().nullable(),
});
export type MarketSavingsScenarioRow = z.infer<typeof MarketSavingsScenarioRowSchema>;

export const MarketSavingsScenarioAggregatesSchema = z.object({
    populationRowCount: z.number().int().nonnegative(),
    comparableRowCount: z.number().int().nonnegative(),
    incompleteRowCount: z.number().int().nonnegative(),
    aboveBaselineCount: z.number().int().nonnegative(),
    baselineSpend: z.number().nonnegative(),
    comparisonSpend: z.number().nonnegative(),
    projectedDelta: z.number(),
    savingsOpportunity: z.number().nonnegative(),
});
export type MarketSavingsScenarioAggregates = z.infer<typeof MarketSavingsScenarioAggregatesSchema>;

const MarketSavingsScatterBoundsSchema = z.object({
    minUnitPrice: z.number().positive(),
    maxUnitPrice: z.number().positive(),
});

export const MarketSavingsScenarioResultSchema = z.object({
    jobKey: z.string().min(1),
    baselineMetric: ReportMetricIdSchema,
    comparisonMetric: ReportMetricIdSchema,
    // Defaults for decoding scenarios cached before the demand delivery end date existed.
    demandDeliveryEndDate: z.iso.date().nullable().default(null),
    aggregates: MarketSavingsScenarioAggregatesSchema,
    topOpportunities: z.array(MarketSavingsScenarioRowSchema).max(MARKET_SAVINGS_TOP_OPPORTUNITY_LIMIT),
    scatter: z.object({
        points: z.array(MarketSavingsScenarioRowSchema).max(MARKET_SAVINGS_SCATTER_LIMIT),
        totalPointCount: z.number().int().nonnegative(),
        bounds: MarketSavingsScatterBoundsSchema.nullable(),
    }),
    table: z.object({
        rows: z.array(MarketSavingsScenarioRowSchema),
        offset: z.number().int().nonnegative(),
        pageSize: z.number().int().positive(),
        totalRowCount: z.number().int().nonnegative(),
        nextOffset: z.number().int().nonnegative().nullable(),
    }),
});
export type MarketSavingsScenarioResult = z.infer<typeof MarketSavingsScenarioResultSchema>;

function sameStringValues(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function sameTimeWindow(left: TimeWindow, right: TimeWindow): boolean {
    return left.from === right.from && left.to === right.to;
}
